import React, { useState, useEffect } from 'react';
import { SlidePanel } from '@/components/ui/slide-panel';
// Dialog components removed - using SlidePanel instead
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  GripVertical,
  FileText,
  Calendar,
  Tag,
  AlertCircle,
  Save,
} from 'lucide-react';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from '@hello-pangea/dnd';
import {
  DispositionConfig,
  getDispositionConfigs,
  groupDispositionsByCategory,
} from '@/services/dispositionService';
import { supabase } from '@/lib/supabaseClient';
import { cn } from '@/lib/utils';

interface DispositionManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DispositionManager: React.FC<DispositionManagerProps> = ({
  isOpen,
  onClose,
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pipelineType, setPipelineType] = useState<'residential' | 'commercial' | 'internal'>('residential');
  const [dispositions, setDispositions] = useState<DispositionConfig[]>([]);
  const [editingDisposition, setEditingDisposition] = useState<DispositionConfig | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDisposition, setNewDisposition] = useState({
    display_name: '',
    code: '',
    group_name: '',
    requires_notes: false,
    requires_date: false,
  });
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [tenantId, setTenantId] = useState<string | null>(null);

  // Fetch user's tenant_id on mount
  useEffect(() => {
    const fetchTenantId = async () => {
      try {
        const { data: user } = await supabase.auth.getUser();
        if (!user?.user?.id) return;

        const { data: profile } = await supabase
          .from('users')
          .select('tenant_id')
          .eq('id', user.user.id)
          .single();

        setTenantId(profile?.tenant_id || null);
      } catch (error) {
        console.error('Error fetching tenant ID:', error);
      }
    };

    fetchTenantId();
  }, []);

  useEffect(() => {
    if (isOpen && tenantId !== null) {
      loadDispositions();
    }
  }, [isOpen, pipelineType, tenantId]);

  const loadDispositions = async () => {
    setLoading(true);
    try {
      const configs = await getDispositionConfigs(pipelineType, tenantId || undefined);
      setDispositions(configs || []);
    } catch (error) {
      console.error('Error loading dispositions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dispositions',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const groupedDispositions = groupDispositionsByCategory(dispositions);

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    const groupName = result.source.droppableId;

    const group = groupedDispositions[groupName];
    if (!group) return;

    const newGroup = Array.from(group);
    const [removed] = newGroup.splice(sourceIndex, 1);
    newGroup.splice(destIndex, 0, removed);

    // Update local state
    const updatedDispositions = dispositions.map(d => {
      const groupIndex = newGroup.findIndex(item => item.id === d.id);
      if (groupIndex !== -1) {
        return { ...d, item_order: groupIndex + 1 };
      }
      return d;
    });

    setDispositions(updatedDispositions);

    // Save to database
    try {
      const updates = newGroup.map((item, index) => ({
        id: item.id,
        item_order: index + 1,
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('project_disposition_configs')
          .update({ item_order: update.item_order })
          .eq('id', update.id);

        if (error) throw error;
      }

      toast({
        title: 'Success',
        description: 'Disposition order updated',
      });
    } catch (error) {
      console.error('Error updating disposition order:', error);
      toast({
        title: 'Error',
        description: 'Failed to update disposition order',
        variant: 'destructive',
      });
      loadDispositions(); // Reload to reset
    }
  };

  const handleToggleActive = async (disposition: DispositionConfig) => {
    try {
      const { error } = await supabase
        .from('project_disposition_configs')
        .update({ is_active: !disposition.is_active })
        .eq('id', disposition.id);

      if (error) throw error;

      setDispositions(prev =>
        prev.map(d =>
          d.id === disposition.id ? { ...d, is_active: !d.is_active } : d
        )
      );

      toast({
        title: 'Success',
        description: `Disposition ${disposition.is_active ? 'deactivated' : 'activated'}`,
      });
    } catch (error) {
      console.error('Error toggling disposition:', error);
      toast({
        title: 'Error',
        description: 'Failed to update disposition status',
        variant: 'destructive',
      });
    }
  };

  const handleUpdateDisposition = async () => {
    if (!editingDisposition) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('project_disposition_configs')
        .update({
          display_name: editingDisposition.display_name,
          requires_notes: editingDisposition.requires_notes,
          requires_date: editingDisposition.requires_date,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingDisposition.id);

      if (error) throw error;

      setDispositions(prev =>
        prev.map(d =>
          d.id === editingDisposition.id ? editingDisposition : d
        )
      );

      setIsEditPanelOpen(false);
      setEditingDisposition(null);
      toast({
        title: 'Success',
        description: 'Disposition updated successfully',
      });
    } catch (error) {
      console.error('Error updating disposition:', error);
      toast({
        title: 'Error',
        description: 'Failed to update disposition',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDisposition = async (disposition: DispositionConfig) => {
    if (disposition.is_system) {
      toast({
        title: 'Error',
        description: 'System dispositions cannot be deleted',
        variant: 'destructive',
      });
      return;
    }

    if (!confirm(`Are you sure you want to delete "${disposition.display_name}"?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('project_disposition_configs')
        .delete()
        .eq('id', disposition.id);

      if (error) throw error;

      setDispositions(prev => prev.filter(d => d.id !== disposition.id));
      toast({
        title: 'Success',
        description: 'Disposition deleted successfully',
      });
    } catch (error) {
      console.error('Error deleting disposition:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete disposition',
        variant: 'destructive',
      });
    }
  };

  const handleAddDisposition = async () => {
    if (!newDisposition.display_name || !newDisposition.code || !newDisposition.group_name) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      // Get the group's order and max item order
      const groupDispositions = dispositions.filter(
        d => d.group_name === newDisposition.group_name
      );
      const groupOrder = groupDispositions[0]?.group_order || 999;
      const maxItemOrder = Math.max(
        0,
        ...groupDispositions.map(d => d.item_order)
      );

      const { data, error } = await supabase
        .from('project_disposition_configs')
        .insert({
          pipeline_type: pipelineType,
          group_name: newDisposition.group_name,
          group_order: groupOrder,
          code: newDisposition.code.toLowerCase().replace(/\s+/g, '_'),
          display_name: newDisposition.display_name,
          item_order: maxItemOrder + 1,
          requires_notes: newDisposition.requires_notes,
          requires_date: newDisposition.requires_date,
          is_active: true,
          is_system: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;

      if (data) {
        setDispositions(prev => [...prev, data]);
        setNewDisposition({
          display_name: '',
          code: '',
          group_name: '',
          requires_notes: false,
          requires_date: false,
        });
        setShowAddForm(false);
        toast({
          title: 'Success',
          description: 'Disposition added successfully',
        });
      }
    } catch (error) {
      console.error('Error adding disposition:', error);
      toast({
        title: 'Error',
        description: 'Failed to add disposition',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SlidePanel
        isOpen={isOpen}
        onClose={onClose}
        title="Manage Dispositions"
        width="lg"
        position="right"
        showBackdrop={true}
        topOffset="3.5rem"
        panelClassName="z-[220] border-l-2 border-t-2 border-border rounded-tl-lg"
        backdropClassName="!left-0 !z-[215] backdrop-blur-none bg-transparent"
        immediateBackdrop={true}
        showCloseButton={true}
        closeOnBackdropClick={true}
        contentClassName="!p-0 !overflow-auto"
      >
        <div className="flex flex-col">
          <div className="p-4 border-b">
            <p className="text-sm text-muted-foreground">
              Configure disposition options for each pipeline type. System dispositions cannot be deleted but can be deactivated.
            </p>
          </div>

          <div className="flex flex-col">
            <div className="px-4 py-3 border-b bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Label htmlFor="pipeline-select" className="text-sm font-medium">
                    Pipeline Type:
                  </Label>
                  <Select
                    value={pipelineType}
                    onValueChange={(value) => setPipelineType(value as 'residential' | 'commercial' | 'internal')}
                  >
                    <SelectTrigger id="pipeline-select" className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="residential">Residential</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="internal">Internal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-xs text-muted-foreground">
                  {dispositions.length} disposition{dispositions.length !== 1 ? 's' : ''}
                </div>
              </div>
            </div>

            <div className="px-4 py-4 space-y-4 overflow-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      onClick={() => setShowAddForm(true)}
                      className="gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add Custom Disposition
                    </Button>
                  </div>

                  {showAddForm && (
                    <div className="border rounded-lg p-4 space-y-4 bg-muted/50">
                      <h4 className="font-medium">Add New Disposition</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Display Name</Label>
                        <Input
                          value={newDisposition.display_name}
                          onChange={(e) =>
                            setNewDisposition(prev => ({
                              ...prev,
                              display_name: e.target.value,
                            }))
                          }
                          placeholder="e.g., Follow Up Required"
                        />
                      </div>
                      <div>
                        <Label>Code</Label>
                        <Input
                          value={newDisposition.code}
                          onChange={(e) =>
                            setNewDisposition(prev => ({
                              ...prev,
                              code: e.target.value,
                            }))
                          }
                          placeholder="e.g., follow_up_required"
                        />
                      </div>
                      <div>
                        <Label>Group</Label>
                        <Input
                          value={newDisposition.group_name}
                          onChange={(e) =>
                            setNewDisposition(prev => ({
                              ...prev,
                              group_name: e.target.value,
                            }))
                          }
                          placeholder="e.g., Follow Up Required"
                        />
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={newDisposition.requires_notes}
                            onCheckedChange={(checked) =>
                              setNewDisposition(prev => ({
                                ...prev,
                                requires_notes: checked,
                              }))
                            }
                          />
                          <Label>Requires Notes</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={newDisposition.requires_date}
                            onCheckedChange={(checked) =>
                              setNewDisposition(prev => ({
                                ...prev,
                                requires_date: checked,
                              }))
                            }
                          />
                          <Label>Requires Date</Label>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowAddForm(false);
                          setNewDisposition({
                            display_name: '',
                            code: '',
                            group_name: '',
                            requires_notes: false,
                            requires_date: false,
                          });
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleAddDisposition}
                        disabled={saving}
                      >
                        {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Add Disposition
                      </Button>
                    </div>
                  </div>
                )}

                <DragDropContext onDragEnd={handleDragEnd}>
                  {Object.entries(groupedDispositions).map(([groupName, items]) => (
                    <div key={groupName} className="space-y-2">
                      <h3 className="font-semibold text-sm text-muted-foreground">
                        {groupName}
                      </h3>
                      <Droppable droppableId={groupName}>
                        {(provided) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="space-y-1"
                          >
                            {items.map((disposition, index) => (
                              <Draggable
                                key={disposition.id}
                                draggableId={disposition.id}
                                index={index}
                                isDragDisabled={!disposition.is_active}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    className={cn(
                                      'flex items-center gap-2 p-2 rounded-lg border bg-background',
                                      snapshot.isDragging && 'shadow-lg',
                                      !disposition.is_active && 'opacity-50'
                                    )}
                                  >
                                    <div
                                      {...provided.dragHandleProps}
                                      className="cursor-move"
                                    >
                                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                                    </div>

                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium">
                                          {disposition.display_name}
                                        </span>
                                        {disposition.requires_notes && (
                                          <FileText className="h-3 w-3 text-muted-foreground" />
                                        )}
                                        {disposition.requires_date && (
                                          <Calendar className="h-3 w-3 text-muted-foreground" />
                                        )}
                                      </div>
                                    </div>

                                    <div className="flex items-center gap-1">
                                      <Switch
                                        checked={disposition.is_active}
                                        onCheckedChange={() => handleToggleActive(disposition)}
                                        aria-label="Toggle active"
                                      />
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => {
                                          setEditingDisposition(disposition);
                                          setIsEditPanelOpen(true);
                                        }}
                                        className="h-8 w-8"
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                      {!disposition.is_system && (
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={() => handleDeleteDisposition(disposition)}
                                          className="h-8 w-8 text-destructive"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  ))}
                </DragDropContext>
              </>
            )}
            </div>
          </div>
        </div>
      </SlidePanel>

      {editingDisposition && (
        <SlidePanel
          isOpen={isEditPanelOpen}
          onClose={() => {
            setIsEditPanelOpen(false);
            setEditingDisposition(null);
          }}
          title="Edit Disposition"
          width="md"
          position="right"
          showBackdrop={true}
          topOffset="3.5rem"
          panelClassName="z-[250] border-l-2 border-t-2 border-border rounded-tl-lg"
          backdropClassName="!left-0 !z-[245] backdrop-blur-none bg-transparent"
          immediateBackdrop={true}
          showCloseButton={true}
          closeOnBackdropClick={true}
        >
          <div className="space-y-4">
            <div>
              <Label>Display Name</Label>
              <Input
                value={editingDisposition.display_name}
                onChange={(e) =>
                  setEditingDisposition(prev =>
                    prev ? { ...prev, display_name: e.target.value } : null
                  )
                }
                disabled={editingDisposition.is_system}
              />
              {editingDisposition.is_system && (
                <p className="text-xs text-muted-foreground mt-1">
                  System disposition names cannot be changed
                </p>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Switch
                  checked={editingDisposition.requires_notes}
                  onCheckedChange={(checked) =>
                    setEditingDisposition(prev =>
                      prev ? { ...prev, requires_notes: checked } : null
                    )
                  }
                />
                <Label>Requires Notes</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={editingDisposition.requires_date}
                  onCheckedChange={(checked) =>
                    setEditingDisposition(prev =>
                      prev ? { ...prev, requires_date: checked } : null
                    )
                  }
                />
                <Label>Requires Date</Label>
              </div>
            </div>
            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditPanelOpen(false);
                  setEditingDisposition(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateDisposition}
                disabled={saving}
                className="flex-1"
              >
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </div>
        </SlidePanel>
      )}
    </>
  );
};